import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { fetchMarginCalculatorResults } from "@/features/strateg-cenowy/margin-calculator/api";
import {
	MV_STALE,
	mvStaleTime,
	mvStaleTimeMulti,
	mvStaleTimeWithLiveTtl,
} from "@/lib/mv-stale-time";
import {
	type BreakdownDimension,
	type DashboardQuery,
	type DetailGrain,
	EXPIRY_RISK_DEFAULT_LIMIT,
	type ExpiryRiskParams,
	fetchDashboardCategoryDetail,
	fetchDashboardCategoryHierarchy,
	fetchDashboardCategoryProducts,
	fetchDashboardCategoryProductsPage,
	fetchDashboardCategorySegments,
	fetchDashboardCennikAnomalies,
	fetchDashboardCentralPricing,
	fetchDashboardCheapestBasket,
	fetchDashboardExpiryRisk,
	fetchDashboardForecast,
	fetchDashboardOverview,
	fetchDashboardPharmacyOptions,
	fetchDashboardPriceSource,
	fetchDashboardScopeBreakdown,
	fetchDashboardSegments,
	fetchDashboardSkuDaily,
	fetchDashboardStockDetail,
	fetchDashboardStockKpi,
	fetchDashboardTrend,
	isLegacyScopeBreakdown,
	NO_CATEGORY_TOKEN,
	normalizeScopeBreakdown,
	PRODUCTS_PAGE_LIMIT,
	SKU_DEFAULT_SORT,
	type SkuListOptions,
	type StockDetailParams,
} from "../api/dashboard-api";

// staleTime = fixed-time do najbliższej granicy refreshu rodziny MV danego
// endpointu (harmonogramy i zmierzone czasy refreshu: MV_STALE w
// lib/mv-stale-time.ts). Endpointy cenowe (central-pricing, anomalies) obok
// MV czytają ŻYWE tabele cenników (`cennik_apteczny_cnkn*`) — dlatego ich
// cache jest dodatkowo ograniczony TTL-em komponentu live; endpointy czysto
// MV (koszyk, forecast bez filtrów) zostają na samych granicach (bez refetch
// stormu).
export const LIVE_CENNIK_TTL_MS = 15 * 60 * 1000;
export const LIVE_SPRZ_TTL_MS = 15 * 60 * 1000;
const STALE_DAILY_SALES_MV = mvStaleTime(MV_STALE.dailySales);
const STALE_SALES_WITH_LIVE_CENNIK = mvStaleTimeWithLiveTtl(
	MV_STALE.dashboardSalesDaily,
	LIVE_CENNIK_TTL_MS,
);
const STALE_DASHBOARD_PURCHASES = mvStaleTime(MV_STALE.dashboardPurchasesDaily);
/** Kalkulator marży czyta własny dzienny MV (4× na dobę). */
const STALE_MARGIN_CALC = mvStaleTime(MV_STALE.marginCalc6h);
/** Magazyn: nocny snapshot raz na dobę — świeży do granicy 02:00 UTC. */
const STALE_STOCK_SNAPSHOT = mvStaleTime(MV_STALE.inventoryDailySnapshot);
/** Lista SKU z magazynem: min(granica MV sprzedaży, granica snapshotu). */
const STALE_SALES_AND_STOCK = mvStaleTimeMulti([
	MV_STALE.dashboardSalesDaily,
	MV_STALE.inventoryDailySnapshot,
]);
/** Sekcja Kategorie/Towary czyta wyłącznie dzienny MV sprzedaży (v297). */
const STALE_DASHBOARD_SALES = mvStaleTime(MV_STALE.dashboardSalesDaily);
/** Forecast z filtrem produktowym liczy z żywego sprz — bounded TTL. */
const STALE_FORECAST_LIVE = mvStaleTimeWithLiveTtl(MV_STALE.dailySales, LIVE_SPRZ_TTL_MS);
const GC = 30 * 60 * 1000;

/**
 * Ciężkie sekcje dashboardu (Przegląd, Źródła ceny, Magazyn, Ryzyko krótkiej
 * daty, lista SKU) — sekundy pracy bazy na jedno żądanie. Retry dokłada drugi
 * taki przebieg do puli, która już się dławi, a refetch po powrocie sieci
 * strzela nimi wszystkimi naraz. Błąd pokazuje karta; użytkownik ponawia
 * zmianą filtra. Reszta dashboardu zostaje na globalnych defaultach
 * z `main.tsx` — tam `refetchOnReconnect` zostaje włączony.
 *
 * Dla listy SKU stawka jest wyższa niż przy zwykłym widgecie: to infinite
 * query, więc jeden powrót sieci przeładowuje WSZYSTKIE załadowane strony
 * naraz. Po dziesięciu kliknięciach „Pokaż więcej” jedno mrugnięcie Wi-Fi
 * kosztuje dziesięć pełnych agregatów MV.
 */
const HEAVY_SECTION = { retry: false, refetchOnReconnect: false } as const;

/** Lustro backendowego `has_product_dimension_filters` (forecast: MV vs live sprz). */
export function hasProductDimensionFilters(p: DashboardQuery): boolean {
	return (
		Boolean(p.category) ||
		p.kosmetyk === true ||
		p.natubay === true ||
		(p.rodzaj != null && p.rodzaj !== "ALL")
	);
}

const dashboardFilterKey = (p: DashboardQuery) =>
	[
		p.scope ?? null,
		p.excludedIdfirms ?? null,
		p.category ?? null,
		p.kosmetyk ?? false,
		p.natubay ?? false,
		p.rodzaj ?? "ALL",
		p.dateFrom ?? null,
		p.dateTo ?? null,
	] as const;

/** Hierarchiczny key factory — invalidacja grupowa: `dashboardKeys.all`. */
export const dashboardKeys = {
	all: ["raporty", "dashboard"] as const,
	pharmacyOptions: (scope: string | undefined) =>
		[...dashboardKeys.all, "pharmacy-options", scope ?? null] as const,
	/**
	 * Przegląd: klucz to same wspólne filtry — okresy porównawcze WYNIKAJĄ
	 * z `dateFrom`/`dateTo` i liczy je serwer, więc nie ma ich czego dokładać
	 * (dołożenie policzonych na kliencie dat rozjechałoby cache z odpowiedzią).
	 */
	overview: (p: DashboardQuery) =>
		[...dashboardKeys.all, "overview", ...dashboardFilterKey(p)] as const,
	/**
	 * Segmenty: te same filtry co Przegląd — okresy i buckety wylicza serwer,
	 * więc nic więcej nie należy do klucza (dołożenie policzonych na kliencie
	 * dat rozjechałoby cache z odpowiedzią).
	 */
	segments: (p: DashboardQuery) =>
		[...dashboardKeys.all, "segments", ...dashboardFilterKey(p)] as const,
	/**
	 * Porównanie w ramach zakresu: filtry Przeglądu + wymiar (`by`). Brak
	 * wymiaru = domyślny dla scope, rozstrzygany przez serwer — `null` w kluczu
	 * jest wtedy osobnym wpisem, bo odpowiedź niesie inny `dimension`.
	 */
	scopeBreakdown: (p: DashboardQuery, by: BreakdownDimension | undefined) =>
		[...dashboardKeys.all, "scope-breakdown", by ?? null, ...dashboardFilterKey(p)] as const,
	/**
	 * Źródła ceny: te same filtry co Przegląd. Klasyfikacja jest BIEŻĄCA i
	 * liczy ją serwer z żywych cenników — nie ma jej czego dokładać do klucza,
	 * a krótszy `staleTime` (komponent live) pilnuje odświeżenia.
	 */
	priceSource: (p: DashboardQuery) =>
		[...dashboardKeys.all, "price-source", ...dashboardFilterKey(p)] as const,
	/**
	 * Marża wg widełek: klucz TYLKO scope + okres — kalkulator marży nie zna
	 * kategorii, kosmetyków ani rodzaju (segment wybiera się z jego komórek
	 * po stronie klienta), więc zmiana tych filtrów nie ma czego refetchować.
	 */
	marginBuckets: (p: DashboardQuery) =>
		[
			...dashboardKeys.all,
			"margin-buckets",
			p.scope ?? null,
			p.excludedIdfirms ?? null,
			p.dateFrom ?? null,
			p.dateTo ?? null,
		] as const,
	/**
	 * Trend: klucz BEZ zakresu dat — okno jest własne (12 miesięcy do D-1) i
	 * ustala je serwer. Gdyby daty tu były, każda zmiana okresu kasowałaby
	 * cache i refetchowała DOKŁADNIE tę samą odpowiedź.
	 */
	trend: (p: DashboardQuery) =>
		[
			...dashboardKeys.all,
			"trend",
			p.scope ?? null,
			p.excludedIdfirms ?? null,
			p.category ?? null,
			p.kosmetyk ?? false,
			p.natubay ?? false,
			p.rodzaj ?? "ALL",
		] as const,
	centralPricing: (p: DashboardQuery) =>
		[...dashboardKeys.all, "central-pricing", ...dashboardFilterKey(p)] as const,
	cennikAnomalies: (p: DashboardQuery, cennikName: string) =>
		[...dashboardKeys.all, "cennik-anomalies", cennikName, ...dashboardFilterKey(p)] as const,
	cheapestBasket: (p: DashboardQuery) =>
		[...dashboardKeys.all, "cheapest-basket", ...dashboardFilterKey(p)] as const,
	forecast: (p: DashboardQuery) =>
		[...dashboardKeys.all, "forecast", ...dashboardFilterKey(p)] as const,
	/**
	 * Magazyn: klucz BEZ zakresu dat — stan jest bieżącym snapshotem, a okno
	 * rotacji jest stałe. Trzymanie dat w kluczu kasowałoby cache przy każdej
	 * zmianie okresu i sugerowało, że okres zmienia stan.
	 */
	stockKpi: (p: DashboardQuery) =>
		[
			...dashboardKeys.all,
			"stock-kpi",
			p.scope ?? null,
			p.excludedIdfirms ?? null,
			p.category ?? null,
			p.kosmetyk ?? false,
			p.natubay ?? false,
			p.rodzaj ?? "ALL",
		] as const,
	/**
	 * Ryzyko krótkiej daty: populacja jak magazyn (bez dat dashboardu) +
	 * horyzont i limit rankingu — wszystko, co czyta `queryFn`, jest w kluczu.
	 */
	expiryRisk: (p: ExpiryRiskParams) =>
		[
			...dashboardKeys.all,
			"expiry-risk",
			p.scope ?? null,
			p.excludedIdfirms ?? null,
			p.category ?? null,
			p.kosmetyk ?? false,
			p.natubay ?? false,
			p.rodzaj ?? "ALL",
			p.horizon,
			p.limit ?? EXPIRY_RISK_DEFAULT_LIMIT,
		] as const,
	/**
	 * Szczegół magazynu: populacja jak kafel + metryka i stan tabeli
	 * (sort/filtr/strona). `dataAsOf` MUSI być w kluczu, mimo że dzień ustala
	 * serwer: przy niezgodności odpowiedź to 422 `STOCK_SNAPSHOT_STALE`, a
	 * naprawa polega na podmianie `dataAsOf` w URL-u. Bez tego pola w kluczu
	 * React Query oddawałby zacacheowany błąd i przycisk „Pokaż aktualny
	 * stan” byłby martwym klikiem (`feedback_tanstack_query_key_consistency`:
	 * wszystko, co czyta `queryFn`, należy do klucza).
	 */
	stockDetail: (p: StockDetailParams) =>
		[
			...dashboardKeys.all,
			"stock-detail",
			p.metric,
			p.dataAsOf ?? null,
			p.scope ?? null,
			p.excludedIdfirms ?? null,
			p.category ?? null,
			p.kosmetyk ?? false,
			p.natubay ?? false,
			p.rodzaj ?? "ALL",
			p.q ?? null,
			p.sortBy ?? null,
			p.sortDir ?? null,
			p.limit ?? null,
			p.offset ?? 0,
		] as const,
	/** Osobny key z multiselectem — inne widgety nie znają `categories`. */
	categoryProducts: (p: DashboardQuery, categories: string[]) =>
		[
			...dashboardKeys.all,
			"category-products",
			categories.join(","),
			...dashboardFilterKey(p),
		] as const,
	/**
	 * Stronicowana lista SKU — inna odpowiedź niż TOP 10, własny cache.
	 * `offset` NIE jest w kluczu: strony trzyma jeden wpis `useInfiniteQuery`,
	 * a zmiana filtra daje inny klucz, czyli pusty wynik od zera.
	 *
	 * Fraza i kolejność SĄ w kluczu, bo obie zmieniają odpowiedź serwera.
	 * Nowy klucz startuje od `initialPageParam: 0`, więc paging wraca na
	 * pierwszą stronę bez ani jednej linijki kodu resetującego offset.
	 */
	categoryProductsPage: (p: DashboardQuery, categories: string[], list?: SkuListOptions) =>
		[
			...dashboardKeys.all,
			"category-products-page",
			categories.join(","),
			list?.q?.trim() ?? "",
			`${list?.sort?.key ?? SKU_DEFAULT_SORT.key}:${list?.sort?.dir ?? SKU_DEFAULT_SORT.dir}`,
			list?.columnFilters ?? "",
			// Atrybuty i populacja zmieniają ZBIÓR wierszy — inny klucz, pusty
			// wynik od zera (jak fraza i filtry kartoteki).
			list?.attrs?.join(",") ?? "",
			list?.sources?.join(",") ?? "",
			list?.contract ?? false,
			list?.pricelistAsOf ?? "",
			list?.pricelistVersion ?? "",
			list?.stockAsOf ?? "",
			list?.universe ?? "sales",
			// Magazyn zmienia odpowiedź (kolumny, mianowniki), więc jest w kluczu;
			// Jawne piny również należą do klucza, bo zmieniają odpowiedź stale.
			list?.withStock ? "stock" : "",
			...dashboardFilterKey(p),
		] as const,
	/**
	 * Dzienny szczegół SKU: klucz niesie BLOZ i populację (kategorie + filtry
	 * Przeglądu). Inny towar albo inna populacja = inny wpis, więc pod nowym
	 * wierszem nigdy nie zostaje stary szereg.
	 */
	skuDaily: (p: DashboardQuery, categories: string[], bloz07: string, list?: SkuListOptions) =>
		[
			...dashboardKeys.all,
			"sku-daily",
			...(list ? [list] : []),
			bloz07,
			categories.join(","),
			...dashboardFilterKey(p),
		] as const,
	/** Magazyn JEDNEGO SKU do rozwinięcia wiersza, gdy lista nie niesie magazynu. */
	skuStock: (p: DashboardQuery, categories: string[], bloz07: string) =>
		[
			...dashboardKeys.all,
			"sku-stock",
			bloz07,
			categories.join(","),
			...dashboardFilterKey(p),
		] as const,
	categoryHierarchy: (p: DashboardQuery, categories: string[]) =>
		[
			...dashboardKeys.all,
			"category-hierarchy",
			categories.join(","),
			...dashboardFilterKey(p),
		] as const,
	/**
	 * Szczegół: klucz niesie populację (`categories`), selekcję (`detail`) i
	 * ziarno — widok roczny to inna odpowiedź (własne okno), więc nie może
	 * dzielić cache z dziennym.
	 */
	categoryDetail: (
		p: DashboardQuery,
		categories: string[],
		detail: string[],
		grain: DetailGrain = "day",
	) =>
		[
			...dashboardKeys.all,
			"category-detail",
			grain,
			categories.join(","),
			detail.join(","),
			...dashboardFilterKey(p),
		] as const,
	/**
	 * Rozbicie kategorii szczegółu na grupy zarobku: populacja (`categories`)
	 * + selekcja (`detail`) + filtry Przeglądu. Bez ziarna — rozbicie jest
	 * jedno na okres.
	 */
	categorySegments: (p: DashboardQuery, categories: string[], detail: string[]) =>
		[
			...dashboardKeys.all,
			"category-segments",
			categories.join(","),
			detail.join(","),
			...dashboardFilterKey(p),
		] as const,
};

/** CSV `categories` z URL → posortowana lista kodów (stabilny query key). */
export function parseCategoriesCsv(raw: string | undefined): string[] {
	if (!raw) return [];
	const codes = raw
		.split(",")
		.map((c) => c.trim())
		.filter((c) => /^[0-9]+$/.test(c));
	return [...new Set(codes)].sort();
}

/**
 * `branch` z URL → ścieżka rozwiniętej gałęzi (kody rozdzielone kropką,
 * np. `08.0801`). BEZ sortowania — kolejność JEST semantyką (rodzic → dziecko);
 * walidację, czy ścieżka istnieje w danych, robi karta (obcina od pierwszego
 * niedopasowania, więc stary deep link nie wywala strony).
 */
export function parseBranchPath(raw: string | undefined): string[] {
	if (!raw) return [];
	return raw
		.split(".")
		.map((c) => c.trim())
		.filter((c) => /^[0-9]+$/.test(c));
}

/**
 * CSV `detail` z URL → posortowana lista tokenów selekcji szczegółu.
 * Dopuszczalne: cyfrowe kody kategorii i jawny `__none` („Bez kategorii”).
 * Sortowanie + dedup dają stabilny query key niezależnie od kolejności klików.
 */
export function parseDetailCsv(raw: string | undefined): string[] {
	if (!raw) return [];
	const tokens = raw
		.split(",")
		.map((c) => c.trim())
		.filter((c) => c === NO_CATEGORY_TOKEN || /^[0-9]+$/.test(c));
	return [...new Set(tokens)].sort();
}

/** Parametry API z search route — loader prefetch i strona. */
export function dashboardQueryFromSearch(search: {
	scope?: string;
	excludedIdfirms?: string;
	dateFrom?: string;
	dateTo?: string;
	category?: string;
	kosmetyk?: boolean;
	natubay?: boolean;
	rodzaj?: DashboardQuery["rodzaj"];
}): DashboardQuery {
	return {
		scope: search.scope,
		excludedIdfirms: search.excludedIdfirms,
		category: search.category,
		kosmetyk: search.kosmetyk,
		natubay: search.natubay,
		rodzaj: search.rodzaj ?? "ALL",
		dateFrom: search.dateFrom,
		dateTo: search.dateTo,
	};
}

export const dashboardOverviewQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.overview(params),
		queryFn: ({ signal }) => fetchDashboardOverview(params, signal),
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		placeholderData: keepPreviousData,
		...HEAVY_SECTION,
	});

export const dashboardPharmacyOptionsQueryOptions = (scope: string | undefined) =>
	queryOptions({
		queryKey: dashboardKeys.pharmacyOptions(scope),
		queryFn: ({ signal }) => fetchDashboardPharmacyOptions(scope, signal),
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
	});

export const dashboardScopeBreakdownQueryOptions = (
	params: DashboardQuery,
	by: BreakdownDimension | undefined,
) =>
	queryOptions({
		queryKey: dashboardKeys.scopeBreakdown(params, by),
		queryFn: ({ signal }) => fetchDashboardScopeBreakdown(params, by, signal),
		// select obejmuje też dane odtworzone z cache, z pominięciem queryFn.
		select: normalizeScopeBreakdown,
		staleTime: (query) =>
			isLegacyScopeBreakdown(query.state.data) ? 0 : STALE_DASHBOARD_SALES(query),
		refetchInterval: (query) =>
			isLegacyScopeBreakdown(query.state.data) && !query.state.error ? 10_000 : false,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardSegmentsQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.segments(params),
		queryFn: ({ signal }) => fetchDashboardSegments(params, signal),
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

/**
 * Źródła ceny to HYBRYDA: sprzedaż z dziennego MV + ŻYWE cenniki centralne.
 * Dlatego cache jest ograniczony także TTL-em komponentu live — inaczej kafle
 * pokrycia wisiałyby do najbliższego refreshu MV, mimo zmiany cennika.
 */
export const dashboardPriceSourceQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.priceSource(params),
		queryFn: ({ signal }) => fetchDashboardPriceSource(params, signal),
		staleTime: STALE_SALES_WITH_LIVE_CENNIK,
		gcTime: GC,
		placeholderData: keepPreviousData,
		...HEAVY_SECTION,
	});

/**
 * Marża wg widełek zakupu = `summary.outsideTop` kalkulatora marży
 * (`/strateg-cenowy/margin-calculator/results`). `limit: 1` — sekcja bierze
 * TYLKO podsumowanie (liczone zawsze na pełnym zbiorze), lista produktów
 * zostaje w kalkulatorze. Świeżość: MV kalkulatora (4× na dobę); backend
 * dodatkowo buforuje wynik 30 min.
 */
export const dashboardMarginBucketsQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.marginBuckets(params),
		queryFn: ({ signal }) =>
			fetchMarginCalculatorResults(
				{
					scope: params.scope ?? "wlasne",
					excludedIdfirms: params.excludedIdfirms,
					dateFrom: params.dateFrom,
					dateTo: params.dateTo,
					limit: 1,
					// Pomija zapytanie o własny cennik apteki (kilkanaście sekund
					// cold na prod) — sekcja czyta wyłącznie `summary.outsideTop`.
					summaryOnly: true,
				},
				signal,
			),
		staleTime: STALE_MARGIN_CALC,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardTrendQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.trend(params),
		queryFn: ({ signal }) => fetchDashboardTrend(params, signal),
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardCentralPricingQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.centralPricing(params),
		queryFn: ({ signal }) => fetchDashboardCentralPricing(params, signal),
		staleTime: STALE_SALES_WITH_LIVE_CENNIK,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardCennikAnomaliesQueryOptions = (
	params: DashboardQuery,
	cennikName: string | null,
) =>
	queryOptions({
		queryKey: dashboardKeys.cennikAnomalies(params, cennikName ?? ""),
		queryFn: ({ signal }) => fetchDashboardCennikAnomalies(params, cennikName as string, signal),
		enabled: Boolean(cennikName),
		staleTime: STALE_SALES_WITH_LIVE_CENNIK,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardCheapestBasketQueryOptions = (params: DashboardQuery, enabled = true) =>
	queryOptions({
		queryKey: dashboardKeys.cheapestBasket(params),
		queryFn: ({ signal }) => fetchDashboardCheapestBasket(params, signal),
		enabled,
		staleTime: STALE_DASHBOARD_PURCHASES,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardCategoryProductsQueryOptions = (
	params: DashboardQuery,
	categories: string[],
) =>
	queryOptions({
		queryKey: dashboardKeys.categoryProducts(params, categories),
		queryFn: ({ signal }) => fetchDashboardCategoryProducts(params, categories, signal),
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

/**
 * Lista SKU stronami po `PRODUCTS_PAGE_LIMIT`. `enabled` domyślnie false —
 * zwinięta zakładka nie strzela w API, pierwsza strona leci dopiero po
 * kliknięciu.
 *
 * BEZ `placeholderData: keepPreviousData`. Ta lista jest podstawą zaznaczenia,
 * udziału i eksportu, więc pokazanie wierszy POPRZEDNIEJ populacji pod nowymi
 * filtrami nie jest kosmetyką: użytkownik zaznaczyłby towary z zakresu A
 * i wyeksportował je jako wynik zakresu B. Zmiana filtra ma dać stan pusty,
 * nie stary.
 *
 * `meta.persist: false` trzyma tę rodzinę POZA localStorage — jedna strona to
 * ~100 wierszy z opisem kartotekowym, a `MAX_BYTES` persistera to 2 MB na
 * cały dashboard.
 */
export const dashboardCategoryProductsPageQueryOptions = (
	params: DashboardQuery,
	categories: string[],
	enabled = false,
	list?: SkuListOptions,
) =>
	infiniteQueryOptions({
		queryKey: dashboardKeys.categoryProductsPage(params, categories, list),
		// Kolejne strony dziedziczą dzień snapshotu pierwszej (`stockAsOf`),
		// więc serwer odrzuci 422 próbę zmieszania dwóch dni zamiast oddać
		// stronę z innego snapshotu niż sumy populacji.
		queryFn: ({ pageParam, signal }) =>
			fetchDashboardCategoryProductsPage(
				params,
				categories,
				{ limit: PRODUCTS_PAGE_LIMIT, offset: pageParam.offset },
				signal,
				{ ...list, ...pageParam.pin },
			),
		initialPageParam: {
			offset: 0,
			pin: {} as Pick<SkuListOptions, "stockAsOf" | "pricelistAsOf" | "pricelistVersion">,
		},
		getNextPageParam: (last) =>
			last.nextOffset == null
				? undefined
				: {
						offset: last.nextOffset,
						pin: {
							...(list?.withStock ? { stockAsOf: last.stockAsOf } : {}),
							...(list?.sources?.length || list?.contract
								? { pricelistAsOf: last.pricelistAsOf, pricelistVersion: last.pricelistVersion }
								: {}),
						},
					},
		enabled,
		// Z magazynem odpowiedź łączy dwa źródła: świeża tylko do BLIŻSZEJ
		// granicy (MV sprzedaży albo snapshot magazynu).
		staleTime: list?.withStock ? STALE_SALES_AND_STOCK : STALE_DASHBOARD_SALES,
		gcTime: GC,
		meta: { persist: false },
		...HEAVY_SECTION,
	});

/**
 * Dzienny szczegół jednego SKU. Strzela DOPIERO po rozwinięciu wiersza
 * (`enabled`), BEZ `keepPreviousData` — szczegół poprzedniego towaru pod
 * nagłówkiem nowego byłby liczbą o innym produkcie. Poza localStorage jak
 * reszta rodziny listy SKU.
 */
export const dashboardSkuDailyQueryOptions = (
	params: DashboardQuery,
	categories: string[],
	bloz07: string,
	list?: SkuListOptions,
) =>
	queryOptions({
		queryKey: dashboardKeys.skuDaily(params, categories, bloz07, list),
		queryFn: ({ signal }) => fetchDashboardSkuDaily(params, categories, bloz07, signal, list),
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		meta: { persist: false },
		...HEAVY_SECTION,
	});

/**
 * Magazyn jednego towaru do rozwinięcia wiersza — TEN SAM endpoint strony
 * z `withStock` i DOKŁADNYM kodem (`bloz`), więc scope, okres, segment
 * i kategorie są identyczne z listą, a odpowiedź ma najwyżej jeden wiersz.
 * Nie fraza-podciąg: „9900001” jako `q` łapie też nazwy z tym ciągiem,
 * a przy ponad stu takich trafieniach kod z dalszej strony w ogóle by nie
 * przyszedł i wyglądałby jak „brak stanu”. Strzela dopiero po rozwinięciu
 * i tylko wtedy, gdy lista sama magazynu nie ma.
 */
export const dashboardSkuStockQueryOptions = (
	params: DashboardQuery,
	categories: string[],
	bloz07: string,
) =>
	queryOptions({
		queryKey: dashboardKeys.skuStock(params, categories, bloz07),
		queryFn: ({ signal }) =>
			fetchDashboardCategoryProductsPage(params, categories, { limit: 1, offset: 0 }, signal, {
				bloz: [bloz07],
				withStock: true,
			}),
		staleTime: STALE_SALES_AND_STOCK,
		gcTime: GC,
		meta: { persist: false },
		...HEAVY_SECTION,
	});

export const dashboardCategoryHierarchyQueryOptions = (
	params: DashboardQuery,
	categories: string[],
) =>
	queryOptions({
		// Klucz BEZ `branch` — rozwijanie gałęzi jest czysto klienckie na
		// pełnej odpowiedzi; branch w kluczu kasowałby cache przy każdym kliku.
		queryKey: dashboardKeys.categoryHierarchy(params, categories),
		queryFn: ({ signal }) => fetchDashboardCategoryHierarchy(params, categories, signal),
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardCategoryDetailQueryOptions = (
	params: DashboardQuery,
	categories: string[],
	detail: string[],
	grain: DetailGrain = "day",
) =>
	queryOptions({
		queryKey: dashboardKeys.categoryDetail(params, categories, detail, grain),
		queryFn: ({ signal }) =>
			fetchDashboardCategoryDetail(params, categories, detail, grain, signal),
		// Pusta selekcja = szczegół zamknięty; nie strzelamy w API.
		enabled: detail.length > 0,
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

/**
 * Rozbicie kategorii szczegółu na grupy zarobku. Strzela DOPIERO, gdy
 * szczegół jest otwarty (`detail` niepuste) — zamknięty szczegół = zero
 * żądań, dokładnie jak `category-detail`.
 */
export const dashboardCategorySegmentsQueryOptions = (
	params: DashboardQuery,
	categories: string[],
	detail: string[],
) =>
	queryOptions({
		queryKey: dashboardKeys.categorySegments(params, categories, detail),
		queryFn: ({ signal }) => fetchDashboardCategorySegments(params, categories, detail, signal),
		enabled: detail.length > 0,
		staleTime: STALE_DASHBOARD_SALES,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardStockKpiQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.stockKpi(params),
		queryFn: ({ signal }) => fetchDashboardStockKpi(params, signal),
		staleTime: STALE_STOCK_SNAPSHOT,
		gcTime: GC,
		placeholderData: keepPreviousData,
		...HEAVY_SECTION,
	});

export const dashboardExpiryRiskQueryOptions = (params: ExpiryRiskParams) => {
	// Limit JEDEN raz, tu — inaczej klucz (`limit ?? null`) i URL (`limit=25`)
	// opisują dwa różne żądania i cache pudłuje przy pierwszym jawnym limicie.
	const withLimit = { ...params, limit: params.limit ?? EXPIRY_RISK_DEFAULT_LIMIT };
	return queryOptions({
		queryKey: dashboardKeys.expiryRisk(withLimit),
		queryFn: ({ signal }) => fetchDashboardExpiryRisk(withLimit, signal),
		staleTime: STALE_STOCK_SNAPSHOT,
		gcTime: GC,
		placeholderData: keepPreviousData,
		...HEAVY_SECTION,
	});
};

export const dashboardStockDetailQueryOptions = (params: StockDetailParams) =>
	queryOptions({
		queryKey: dashboardKeys.stockDetail(params),
		queryFn: ({ signal }) => fetchDashboardStockDetail(params, signal),
		staleTime: STALE_STOCK_SNAPSHOT,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});

export const dashboardForecastQueryOptions = (params: DashboardQuery) =>
	queryOptions({
		queryKey: dashboardKeys.forecast(params),
		queryFn: ({ signal }) => fetchDashboardForecast(params, signal),
		// Frontend może wejść przed backendem: odpowiedź bez obrotu nie jest
		// kompletna, nawet jeśli zapisano ją już pod nową wersją aplikacji.
		staleTime: (query) =>
			query.state.data && !query.state.data.turnover
				? 0
				: (hasProductDimensionFilters(params) ? STALE_FORECAST_LIVE : STALE_DAILY_SALES_MV)(query),
		refetchInterval: (query) =>
			query.state.data && !query.state.data.turnover && !query.state.error ? 10_000 : false,
		gcTime: GC,
		placeholderData: keepPreviousData,
	});
